/**
 * Formulaire de configuration TOTP
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Formulaire pour configurer l'authentification TOTP (Google Authenticator).
 */

 // Client Component pour Next.js

import { useState, useEffect } from 'react';
import { use2FA } from '../../hooks';
import { QRCodeDisplay } from '../ui/QRCodeDisplay';
import { BackupCodesDisplay } from '../ui/BackupCodesDisplay';
import { ButtonSpinner } from '../ui/LoadingSpinner';

/**
 * Props du composant TOTPSetupForm
 */
export interface TOTPSetupFormProps {
  /** ID de l'utilisateur */
  userId: string;
  /** Nom de l'application (affiché dans le QR code) */
  appName?: string;
  /** Callback après activation réussie */
  onSuccess?: (backupCodes: string[]) => void;
  /** Callback en cas d'erreur */
  onError?: (error: string) => void;
  /** Callback pour annuler */
  onCancel?: () => void;
  /** Classes CSS additionnelles */
  className?: string;
}

/**
 * Étapes du formulaire
 */
type SetupStep = 'generate' | 'verify' | 'backup';

/**
 * Composant TOTPSetupForm
 *
 * @example
 * ```tsx
 * function TwoFactorSettings() {
 *   const { user } = useAuth();
 *
 *   return (
 *     <TOTPSetupForm
 *       userId={user.id}
 *       appName="Mon Application"
 *       onSuccess={(codes) => {
 *         toast.success('2FA activé avec succès');
 *         // Sauvegarder les codes
 *       }}
 *     />
 *   );
 * }
 * ```
 */
export function TOTPSetupForm({
  userId,
  appName = 'Application',
  onSuccess,
  onError,
  onCancel,
  className = '',
}: TOTPSetupFormProps) {
  const {
    generateTOTPSecret,
    activate2FA,
    isLoading,
    error,
    qrCodeUrl,
    secret,
    backupCodes,
    clearError,
    clearData,
  } = use2FA();

  const [step, setStep] = useState<SetupStep>('generate');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [hasGeneratedSecret, setHasGeneratedSecret] = useState(false);

  // Générer le secret TOTP au montage - UNE SEULE FOIS
  useEffect(() => {
    if (step === 'generate' && !hasGeneratedSecret && !qrCodeUrl) {
      handleGenerateSecret();
      setHasGeneratedSecret(true);
    }
  }, [step]);

  /**
   * Générer le secret TOTP
   */
  const handleGenerateSecret = async () => {
    clearError();
    try {
      await generateTOTPSecret();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de génération du secret';
      onError?.(message);
    }
  };

  /**
   * Vérifier le code et activer 2FA
   */
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setCodeError(null);

    // Validation du code
    if (!verificationCode) {
      setCodeError('Le code est requis');
      return;
    }

    if (!/^\d{6}$/.test(verificationCode)) {
      setCodeError('Le code doit contenir 6 chiffres');
      return;
    }

    try {
      const codes = await activate2FA('APPLICATION', verificationCode, secret || undefined);

      if (codes && codes.length > 0) {
        setStep('backup');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Code invalide';
      setCodeError(message);
      onError?.(message);
    }
  };

  /**
   * Finaliser la configuration
   */
  const handleFinish = () => {
    onSuccess?.(backupCodes);
    clearData();
  };

  /**
   * Annuler la configuration
   */
  const handleCancel = () => {
    clearData();
    onCancel?.();
  };

  return (
    <div className={`w-full max-w-2xl ${className}`}>
      {/* Indicateur d'étapes */}
      <div className="mb-8">
        <div className="flex items-center justify-center">
          {(['generate', 'verify', 'backup'] as SetupStep[]).map((s, index) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                  step === s
                    ? 'bg-primary text-content-inverted'
                    : index < ['generate', 'verify', 'backup'].indexOf(step)
                    ? 'bg-success text-content-inverted'
                    : 'bg-surface-overlay text-content-muted'
                }`}
              >
                {index < ['generate', 'verify', 'backup'].indexOf(step) ? '✓' : index + 1}
              </div>
              {index < 2 && (
                <div
                  className={`w-24 h-1 ${
                    index < ['generate', 'verify', 'backup'].indexOf(step)
                      ? 'bg-success'
                      : 'bg-surface-overlay'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2 px-4">
          <span className="text-xs text-content-muted">Scanner</span>
          <span className="text-xs text-content-muted">Vérifier</span>
          <span className="text-xs text-content-muted">Codes backup</span>
        </div>
      </div>

      {/* Erreur globale */}
      {error && (
        <div
          className="mb-6 p-4 bg-danger-light border border-danger rounded-lg"
          role="alert"
        >
          <p className="text-sm text-danger">{error}</p>
        </div>
      )}

      {/* Étape 1 : Afficher le QR code */}
      {step === 'generate' && qrCodeUrl && secret && (
        <div className="space-y-6">
          <QRCodeDisplay
            qrCodeUrl={qrCodeUrl}
            secret={secret}
            appName={appName}
          />

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-border rounded-lg text-sm font-medium text-content-muted bg-surface hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={() => setStep('verify')}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-content-inverted bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
            >
              Suivant
            </button>
          </div>
        </div>
      )}

      {/* Étape 2 : Vérifier le code */}
      {step === 'verify' && (
        <form onSubmit={handleVerify} className="space-y-6">
          <div className="bg-surface rounded-lg border border-border p-6">
            <div className="text-center mb-6">
              <div className="mx-auto w-12 h-12 bg-accent-light rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-6 h-6 text-accent"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-content">
                Vérifier la configuration
              </h3>
              <p className="mt-2 text-sm text-content-muted">
                Entrez le code à 6 chiffres généré par votre application d'authentification
              </p>
            </div>

            <div>
              <label
                htmlFor="verification-code"
                className="block text-sm font-medium text-content-muted mb-2"
              >
                Code de vérification
              </label>
              <input
                id="verification-code"
                type="text"
                value={verificationCode}
                onChange={(e) => {
                  setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                  setCodeError(null);
                }}
                disabled={isLoading}
                maxLength={6}
                className={`w-full px-4 py-3 border rounded-lg text-center text-2xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 ${
                  codeError
                    ? 'border-danger'
                    : 'border-border'
                } bg-surface text-content`}
                placeholder="000000"
                autoComplete="off"
                inputMode="numeric"
              />
              {codeError && (
                <p className="mt-2 text-sm text-danger text-center">
                  {codeError}
                </p>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setStep('generate')}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-border rounded-lg text-sm font-medium text-content-muted bg-surface hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
            >
              Retour
            </button>
            <button
              type="submit"
              disabled={isLoading || verificationCode.length !== 6}
              className="flex-1 flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-content-inverted bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <ButtonSpinner className="mr-2" />
                  Vérification...
                </>
              ) : (
                'Activer 2FA'
              )}
            </button>
          </div>
        </form>
      )}

      {/* Étape 3 : Afficher les codes de backup */}
      {step === 'backup' && backupCodes.length > 0 && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="mx-auto w-12 h-12 bg-success-light rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-6 h-6 text-success"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-content">
              Authentification à deux facteurs activée !
            </h3>
            <p className="mt-2 text-sm text-content-muted">
              Sauvegardez ces codes de backup en lieu sûr
            </p>
          </div>

          <BackupCodesDisplay codes={backupCodes} />

          <button
            type="button"
            onClick={handleFinish}
            className="w-full px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-content-inverted bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Terminer
          </button>
        </div>
      )}

      {/* Chargement initial */}
      {step === 'generate' && !qrCodeUrl && (
        <div className="text-center py-12">
          <ButtonSpinner className="mx-auto mb-4" />
          <p className="text-sm text-content-muted">
            Génération du QR code...
          </p>
        </div>
      )}
    </div>
  );
}
